import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Percent, Plus, Trash2, Check } from "lucide-react";

export default function CommissionRates() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [nu, setNu] = useState({ seller: "", percent: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/commission-rates")
      .then((r) => { setList(r.data.list || []); setEdits({}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveRow = async (row) => {
    const pct = edits[row.seller];
    if (pct === undefined || pct === "" || isNaN(Number(pct))) { toast.error("Enter a valid %."); return; }
    setBusy(true);
    try {
      await api.post("/commission-rates", { seller: row.display, percent: Number(pct) });
      toast.success(`${row.display} updated to ${Number(pct)}%.`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const addRow = async (e) => {
    e.preventDefault();
    if (!nu.seller.trim() || nu.percent === "" || isNaN(Number(nu.percent))) { toast.error("Enter supplier and a valid %."); return; }
    setBusy(true);
    try {
      await api.post("/commission-rates", { seller: nu.seller.trim(), percent: Number(nu.percent) });
      toast.success(`${nu.seller.trim()} added at ${Number(nu.percent)}%.`);
      setNu({ seller: "", percent: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove commission rate for ${row.display}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/commission-rates/${encodeURIComponent(row.seller)}`);
      toast.success(`${row.display} removed.`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="commission-rates-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Settings</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1 flex items-center gap-2">
          <Percent className="w-7 h-7 text-[#D95D39]" /> Supplier Commission
        </h1>
        <p className="text-sm text-[#8CA3A7] mt-1">Default commission % applied to new orders (and shown on the New Order form) for each supplier. {canEdit ? "Edit below — changes apply to future orders." : "View only."}</p>
      </div>

      {canEdit && (
        <form onSubmit={addRow} data-testid="add-rate-form" className="bg-white border border-[#E6E4DD] rounded-sm p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Supplier name</label>
            <input data-testid="new-rate-seller" value={nu.seller} onChange={(e) => setNu({ ...nu, seller: e.target.value })} placeholder="e.g. ADITYA EXPORTS" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <div className="w-32">
            <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Commission %</label>
            <input data-testid="new-rate-percent" type="number" step="0.01" value={nu.percent} onChange={(e) => setNu({ ...nu, percent: e.target.value })} placeholder="1.5" className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
          </div>
          <button data-testid="add-rate-btn" disabled={busy} className="bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-[#2A3447] transition-colors disabled:opacity-60">
            <Plus className="w-4 h-4" /> Add / Update
          </button>
        </form>
      )}

      <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
              <th className="px-4 py-3">Supplier</th>
              <th className="px-4 py-3 w-40 text-right">Commission %</th>
              {canEdit && <th className="px-4 py-3 w-32"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canEdit ? 3 : 2} className="px-4 py-8 text-center text-[#8CA3A7] font-mono text-sm">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={canEdit ? 3 : 2} className="px-4 py-8 text-center text-[#8CA3A7] font-mono text-sm">No commission rates yet.</td></tr>
            ) : list.map((row) => {
              const val = edits[row.seller] !== undefined ? edits[row.seller] : row.percent;
              const dirty = edits[row.seller] !== undefined && Number(edits[row.seller]) !== row.percent;
              return (
                <tr key={row.seller} data-testid={`rate-row-${row.seller}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 text-[#0A0D14] font-medium">{row.display}</td>
                  <td className="px-4 py-3 text-right">
                    {canEdit ? (
                      <div className="flex items-center justify-end gap-1">
                        <input
                          data-testid={`rate-input-${row.seller}`}
                          type="number" step="0.01" value={val}
                          onChange={(e) => setEdits({ ...edits, [row.seller]: e.target.value })}
                          className="w-24 rounded-sm border border-[#E6E4DD] px-2 py-1.5 text-sm font-mono text-right focus:outline-none focus:ring-1 focus:ring-[#1C2331]"
                        />
                        <span className="text-[#8CA3A7] text-xs">%</span>
                      </div>
                    ) : (
                      <span className="font-mono">{row.percent}%</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`save-rate-${row.seller}`} onClick={() => saveRow(row)} disabled={busy || !dirty} className={`p-1.5 rounded-sm transition-colors ${dirty ? "text-[#065F46] hover:bg-emerald-50" : "text-[#CBD5E1] cursor-default"}`} title="Save">
                          <Check className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                        <button data-testid={`delete-rate-${row.seller}`} onClick={() => removeRow(row)} disabled={busy} className="text-[#991B1B] hover:bg-red-50 p-1.5 rounded-sm transition-colors" title="Remove">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
